import {Button} from 'react-bootstrap'
import {useDropAttributes} from '../../hooks/useDropAttributes'
import {CellRenderedProps, ConvertedHoursOfOperation} from '../../types'
import {Fragment, useState} from 'react'
import {alpha, useTheme} from '@mui/material'
import {getHoursOfOperationOnDay} from '../../helpers/generals'
import {boardShading} from '../../helpers/availability'
import {format} from 'date-fns'
import {
  useAuth,
  hasPermission,
  canManageLockedSchedules,
  LABOR_SCHEDULING_PERMS,
} from '../../../auth'

interface CellProps {
  day: Date
  start: Date
  height: number
  end: Date
  hoursOfOperations: ConvertedHoursOfOperation | null
  // This resource's availability at this half hour (AVAILABILITY_CODE). Passed
  // in already resolved rather than looked up here: Cell renders ~1,200 times
  // per grid, so the lookup happens once per cell in Week, not per subscriber.
  availabilityCode?: number
  // Overrides the tooltip for the shading. A whole-day cell (employee × day)
  // summarises several hours, so "09:00 AM — employee unavailable" would name
  // the wrong hour; the summary passes the real ranges instead.
  availabilityTitle?: string | null
  draggable: boolean
  resourceKey: string
  resourceVal: string | number
  positionKey: string
  positionVal: string | number
  cellRenderer?(props: CellRenderedProps): JSX.Element
  children?: JSX.Element
  handleMouseDown?(e: React.MouseEvent<HTMLButtonElement>, otherProps: any): void
  handleMouseUp?(e: React.MouseEvent<HTMLButtonElement>, otherProps: any): void
  handleMouseEnter?(e: React.MouseEvent<HTMLButtonElement>, otherProps: any): void
  handleMouseLeave?(e: React.MouseEvent<HTMLButtonElement>, otherProps: any): void
}

const Cell = ({
  day,
  start,
  end,
  hoursOfOperations,
  availabilityCode,
  availabilityTitle,
  draggable,
  resourceKey,
  resourceVal,
  cellRenderer,
  height,
  children,
  positionKey,
  positionVal,
  ...extraProps
}: CellProps) => {
  const props = useDropAttributes({
    start,
    end,
    day,
    resourceKey,
    resourceVal,
    positionKey,
    positionVal,
  })
  const {currentUser} = useAuth()
  const {handleMouseDown, handleMouseUp, handleMouseEnter, handleMouseLeave, ...otherProps} =
    extraProps

  const onMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (handleMouseDown) {
      handleMouseDown(e, {day, start, end, resourceKey, resourceVal, positionKey, positionVal})
    }
  }

  const onMouseUp = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (handleMouseUp) {
      handleMouseUp(e, {day, start, end, resourceKey, resourceVal, positionKey, positionVal})
    }
  }

  const onMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (handleMouseEnter) {
      handleMouseEnter(e, {day, start, end, resourceKey, resourceVal, positionKey, positionVal})
    }
  }

  const onMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (handleMouseLeave) {
      handleMouseLeave(e, {day, start, end, resourceKey, resourceVal, positionKey, positionVal})
    }
  }

  const multiSelectProps = {
    onMouseDown,
    onMouseUp,
    onMouseOver: onMouseEnter,
    onMouseOut: onMouseLeave,
  }

  if (cellRenderer) {
    return cellRenderer({
      day,
      start,
      end,
      height,
      ...props,
      ...otherProps,
    })
  }
  const hoursOfOperation = getHoursOfOperationOnDay(day, hoursOfOperations)
  // if start and end is not inside the hours of operation return null
  if (
    (start < hoursOfOperation[0] || start >= hoursOfOperation[1]) &&
    (end <= hoursOfOperation[0] || end > hoursOfOperation[1])
  ) {
    return (
      <button
        style={{
          backgroundColor: 'var(--bs-gray-400)',
          padding: 0,
          width: '100%',
          height: '100%',
          cursor: 'not-allowed!important',
          opacity: 'var(--bs-btn-disabled-opacity)',
        }}
        className='btn'
      >
        {children}
      </button>
    )
  }

  // Schedule approvers may fill past slots and locked weeks — but never
  // without the create/edit permission itself.
  const canModifyShifts = hasPermission(
    currentUser,
    LABOR_SCHEDULING_PERMS.create,
    LABOR_SCHEDULING_PERMS.edit
  )
  if (
    (!draggable || start < new Date()) &&
    !(canModifyShifts && canManageLockedSchedules(currentUser))
  ) {
    return (
      <button
        style={{
          backgroundColor: 'var(--bs-secondary)',
          padding: 0,
          width: '100%',
          height: '100%',
          cursor: 'not-allowed!important',
          opacity: 'var(--bs-btn-disabled-opacity)',
        }}
        className='btn'
      >
        {children}
      </button>
    )
  }

  // Availability shading. Deliberately subordinate to the closed-hours and
  // past/locked states above (which return early): those say the cell can't be
  // used at all, this only says who shouldn't be put in it.
  const availabilityStyle = boardShading(availabilityCode)

  return (
    <button
      className='btn btn-light text-gray-700'
      style={{padding: 0, ...availabilityStyle.style}}
      title={
        availabilityTitle ||
        (availabilityStyle.label
          ? `${format(start, 'hh:mm a')} — ${availabilityStyle.label}`
          : `${format(start, 'hh:mm a')}`)
      }
      {...props}
      {...otherProps}
      {...multiSelectProps}
    >
      {children}
    </button>
  )
}

export default Cell
